import type {
  ConversationWithPreview,
  MessageWithDetails,
  TypingUser,
} from '@/types/messaging';

export interface ScheduledMessage {
  id: string;
  conversationId: string;
  content: string;
  scheduledAt: string;
  createdAt: string;
}

export const MAX_CACHED_CONVERSATIONS = 5;
export const MAX_MESSAGES_PER_CONVERSATION = 200;

/** Evict oldest non-protected conversation message caches when over the limit. */
export function evictStaleConversations(
  map: Record<string, MessageWithDetails[]>,
  protectedIds: Set<string>,
  max: number,
): Record<string, MessageWithDetails[]> {
  const keys = Object.keys(map);
  if (keys.length <= max) return map;

  const evictable = keys.filter((k) => !protectedIds.has(k));
  const toRemove = evictable.slice(0, keys.length - max);
  if (toRemove.length === 0) return map;

  const result = { ...map };
  for (const key of toRemove) {
    delete result[key];
  }
  return result;
}

export interface MessagingState {
  conversations: ConversationWithPreview[];
  activeConversationId: string | null;
  messagesByConversation: Record<string, MessageWithDetails[]>;
  typingUsers: Record<string, TypingUser[]>;
  unreadCounts: Record<string, number>;
  totalUnread: number;
  isConnected: boolean;
  onlineUsers: Set<string>;
  isPanelOpen: boolean;
  isNewConversationModalOpen: boolean;
  replyingTo: Record<string, MessageWithDetails | null>;
  drafts: Record<string, string>;
  pinnedMessages: Record<string, MessageWithDetails[]>;
  searchQuery: string;
  searchResults: MessageWithDetails[];
  isSearching: boolean;
  conversationFilter: 'all' | 'direct' | 'group' | 'incident';
  conversationSearch: string;
  mutedConversations: Set<string>;
  scheduledMessages: ScheduledMessage[];

  setConversations: (conversations: ConversationWithPreview[]) => void;
  addConversation: (conversation: ConversationWithPreview) => void;
  updateConversation: (conversationId: string, updates: Partial<ConversationWithPreview>) => void;
  removeConversation: (conversationId: string) => void;
  setActiveConversation: (conversationId: string | null) => void;

  setMessages: (conversationId: string, messages: MessageWithDetails[]) => void;
  addMessage: (conversationId: string, message: MessageWithDetails) => void;
  updateMessage: (conversationId: string, messageId: string, updates: Partial<MessageWithDetails>) => void;
  removeMessage: (conversationId: string, messageId: string) => void;
  prependMessages: (conversationId: string, messages: MessageWithDetails[]) => void;

  setTypingUsers: (conversationId: string, users: TypingUser[]) => void;
  addTypingUser: (conversationId: string, user: TypingUser) => void;
  removeTypingUser: (conversationId: string, userId: string) => void;

  setUnreadCount: (conversationId: string, count: number) => void;
  incrementUnreadCount: (conversationId: string) => void;
  clearUnreadCount: (conversationId: string) => void;
  setTotalUnread: (count: number) => void;

  setConnected: (connected: boolean) => void;

  setOnlineUsers: (userIds: string[]) => void;
  addOnlineUser: (userId: string) => void;
  removeOnlineUser: (userId: string) => void;
  isUserOnline: (userId: string) => boolean;

  setPanelOpen: (open: boolean) => void;
  togglePanel: () => void;
  setNewConversationModalOpen: (open: boolean) => void;

  setReplyingTo: (conversationId: string, message: MessageWithDetails | null) => void;
  clearReplyingTo: (conversationId: string) => void;

  setDraft: (conversationId: string, content: string) => void;
  clearDraft: (conversationId: string) => void;
  getDraft: (conversationId: string) => string;

  setPinnedMessages: (conversationId: string, messages: MessageWithDetails[]) => void;
  addPinnedMessage: (conversationId: string, message: MessageWithDetails) => void;
  removePinnedMessage: (conversationId: string, messageId: string) => void;

  setSearchQuery: (query: string) => void;
  setSearchResults: (results: MessageWithDetails[]) => void;
  setIsSearching: (searching: boolean) => void;
  clearSearch: () => void;

  setConversationFilter: (filter: 'all' | 'direct' | 'group' | 'incident') => void;
  setConversationSearch: (search: string) => void;

  muteConversation: (conversationId: string) => void;
  unmuteConversation: (conversationId: string) => void;
  toggleMuteConversation: (conversationId: string) => void;
  isConversationMuted: (conversationId: string) => boolean;

  addScheduledMessage: (message: Omit<ScheduledMessage, 'id' | 'createdAt'>) => void;
  removeScheduledMessage: (id: string) => void;
  getScheduledMessages: (conversationId: string) => ScheduledMessage[];

  clearMessages: (conversationId: string) => void;
  clearAllState: () => void;
}
